import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Divider,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  Print,
  Close,
  RestaurantMenuOutlined,
  PersonOutlined,
  PhoneOutlined,
  ReceiptLong,
  TableBar,
  ShoppingBag,
  DeliveryDining,
  AccessTime,
  Favorite,
  SpeakerNotes,
} from "@mui/icons-material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import ImageLoader from "components/ImageLoader/ImageLoader";
import { useUserWithCompany } from "../../../hooks/useCompany";
import { formatCurrency } from "../../../utils/currencyUtils";
import { LUXURY, getMenuCartChrome } from "data/guestLuxuryTheme";

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
   THERMAL RECEIPT  (hidden on screen, visible only when printing)
â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const PrintableReceipt = React.forwardRef(
  ({ orderResult, currency = "Rs.", companyInfo }, ref) => {
    if (!orderResult) return null;

    const subtotal = parseFloat(orderResult.subtotal || 0);
    const serviceCharge = parseFloat(orderResult.serviceChargeAmount || 0);
    const vatAmount = parseFloat(orderResult.vatAmount || 0);
    const discountAmount = parseFloat(orderResult.discountAmount || 0);
    const totalAmount = parseFloat(orderResult.totalAmount || 0);

    const orderTypeLabel =
      orderResult.orderType === "dine-in"
        ? "Dine In"
        : orderResult.orderType === "takeaway"
          ? "Takeaway"
          : "Delivery";

    return (
      <Box
        ref={ref}
        sx={{
          width: "80mm",
          fontFamily: "monospace",
          fontSize: "12px",
          p: 2,
          bgcolor: "white",
          color: "black",
          "@media print": { display: "block" },
          "@media screen": { display: "none" },
        }}
      >
        {/* Company Header */}
        <Box sx={{ textAlign: "center", mb: 1.5 }}>
          {companyInfo?.name && (
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {companyInfo.name}
            </Typography>
          )}
          {companyInfo?.address && (
            <Typography sx={{ fontSize: "10px", mt: 0.5 }}>
              {companyInfo.address}
            </Typography>
          )}
          {companyInfo?.phone && (
            <Typography sx={{ fontSize: "10px" }}>
              Tel: {companyInfo.phone}
            </Typography>
          )}
          {companyInfo?.taxId && (
            <Typography sx={{ fontSize: "10px" }}>
              VAT Reg: {companyInfo.taxId}
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderStyle: "dashed", my: 1 }} />

        {/* Order Info */}
        <Box sx={{ mb: 1.5 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
              Order #:
            </Typography>
            <Typography sx={{ fontSize: "11px", fontWeight: 900 }}>
              {orderResult.orderNumber}
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
              Date:
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {moment(orderResult.createdAt).format("MMM DD, YYYY hh:mm A")}
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
              Type:
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>{orderTypeLabel}</Typography>
          </Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
              Customer:
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {orderResult.customerName}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
              Mobile:
            </Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {orderResult.customerMobile}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderStyle: "dashed", my: 1 }} />

        {/* Items */}
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700, mb: 1 }}>
            ITEMS
          </Typography>
          {(orderResult.OrderItems || []).map((item, idx) => {
            const unitPrice = parseFloat(item.unitPrice || 0);
            const totalPrice = parseFloat(item.totalPrice || 0);
            const itemName =
              item.MenuItem?.name || item.menuItemId?.slice(0, 8) || "Item";
            return (
              <Box key={idx} sx={{ mb: 0.75 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    sx={{ fontSize: "11px", fontWeight: 700, flex: 1 }}
                  >
                    {itemName}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
                    {formatCurrency(totalPrice, currency, { space: true })}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "10px", color: "#666" }}>
                  {item.quantity} x {formatCurrency(unitPrice, currency, { space: true })}
                </Typography>
                {item.OrderItemModifiers?.length > 0 && (
                  <Typography sx={{ fontSize: "10px", color: "#666" }}>
                    + {item.OrderItemModifiers.map((m) => m.name).join(", ")}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ borderStyle: "dashed", my: 1 }} />

        {/* Totals */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography sx={{ fontSize: "11px" }}>Subtotal</Typography>
            <Typography sx={{ fontSize: "11px" }}>
              {formatCurrency(subtotal, currency, { space: true })}
            </Typography>
          </Box>
          {serviceCharge > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontSize: "11px" }}>Service Charge</Typography>
              <Typography sx={{ fontSize: "11px" }}>
                {formatCurrency(serviceCharge, currency, { space: true })}
              </Typography>
            </Box>
          )}
          {vatAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontSize: "11px" }}>VAT</Typography>
              <Typography sx={{ fontSize: "11px" }}>
                {formatCurrency(vatAmount, currency, { space: true })}
              </Typography>
            </Box>
          )}
          {discountAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography sx={{ fontSize: "11px" }}>Discount</Typography>
              <Typography sx={{ fontSize: "11px" }}>
                - {formatCurrency(discountAmount, currency, { space: true })}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 900 }}>
            TOTAL
          </Typography>
          <Typography sx={{ fontSize: "13px", fontWeight: 900 }}>
            {formatCurrency(totalAmount, currency, { space: true })}
          </Typography>
        </Box>

        {orderResult.specialInstructions && (
          <>
            <Divider sx={{ borderStyle: "dashed", my: 1 }} />
            <Typography sx={{ fontSize: "10px", fontWeight: 700 }}>
              Notes:
            </Typography>
            <Typography sx={{ fontSize: "10px" }}>
              {orderResult.specialInstructions}
            </Typography>
          </>
        )}

        <Divider sx={{ borderStyle: "dashed", my: 1.5 }} />
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
            Thank you for your order!
          </Typography>
          <Typography sx={{ fontSize: "10px", color: "#666", mt: 0.5 }}>
            Please keep this receipt for your records.
          </Typography>
        </Box>
      </Box>
    );
  },
);
PrintableReceipt.displayName = "PrintableReceipt";

// ─────────────────────────────────────────────────────────────
//  ORDER SUCCESS DIALOG  – clean single-column centered layout
// ─────────────────────────────────────────────────────────────
const OrderSuccessDialog = ({
  luxury = false,
  open,
  onClose,
  orderResult,
  companyInfo: companyInfoProp,
  companyId: companyIdProp,
}) => {
  const theme = useTheme();
  const chrome = getMenuCartChrome(luxury);
  const navigate = useNavigate();
  const { currency, company } = useUserWithCompany();
  const printRef = useRef();

  const resolvedCompany = companyInfoProp || company;
  const activeCurrency = resolvedCompany?.currency || currency || "Rs.";
  const trackCompanyId = companyIdProp || orderResult?.companyId || resolvedCompany?.id;
  const trackOrderId = orderResult?.id || orderResult?.orderId;

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Receipt - ${orderResult?.orderNumber || "ORDER"}`,
  });

  const handleTrackOrder = () => {
    if (!trackOrderId || !trackCompanyId) return;
    const orderNumber = orderResult?.orderNumber;
    const orderNumberQuery = orderNumber
      ? `&orderNo=${encodeURIComponent(orderNumber)}`
      : "";
    navigate(
      `/track-order/${trackOrderId}?companyId=${encodeURIComponent(trackCompanyId)}${orderNumberQuery}`,
    );
  };

  if (!orderResult) return null;

  const subtotal = parseFloat(orderResult.subtotal || 0);
  const serviceCharge = parseFloat(orderResult.serviceChargeAmount || 0);
  const vatAmount = parseFloat(orderResult.vatAmount || 0);
  const discountAmount = parseFloat(orderResult.discountAmount || 0);
  const totalAmount = parseFloat(orderResult.totalAmount || 0);

  const orderTypeConfig = {
    "dine-in": {
      label: "Dine In",
      Icon: TableBar,
      bg: "#e8f5e9",
      color: "#2e7d32",
    },
    takeaway: {
      label: "Takeaway",
      Icon: ShoppingBag,
      bg: "#e3f2fd",
      color: "#1565c0",
    },
    delivery: {
      label: "Delivery",
      Icon: DeliveryDining,
      bg: "#fff3e0",
      color: "#e65100",
    },
  };
  const otc =
    orderTypeConfig[orderResult.orderType] || orderTypeConfig["dine-in"];
  const OrderTypeIcon = otc.Icon;

  return (
    <>
      <PrintableReceipt
        ref={printRef}
        orderResult={orderResult}
        currency={activeCurrency}
        companyInfo={resolvedCompany}
      />

      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: luxury
              ? LUXURY.page
              : theme.palette.mode === "dark"
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* ── TOP APP BAR ── */}
        <Box
          sx={{
            flexShrink: 0,
            background: luxury
              ? LUXURY.barGradient
              : "linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #43a047 100%)",
            px: { xs: 2, sm: 3 },
            py: 1.75,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CheckCircle sx={{ fontSize: 24, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
            >
              Order Confirmed!
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.78)" }}
            >
              We have received your order and it is being prepared
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.18)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.3)" },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* ── SCROLLABLE CONTENT ── */}
        <DialogContent sx={{ p: 0, flex: 1, overflowY: "auto" }}>
          <Box
            sx={{
              maxWidth: 680,
              mx: "auto",
              px: { xs: 1.5, sm: 2 },
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {/* ═══ ORDER OVERVIEW CARD ═══ */}
            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: theme.palette.background.paper,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              {/* Green top strip */}
              <Box
                sx={{
                  background:
                    "linear-gradient(90deg, #1b5e20 0%, #2e7d32 100%)",
                  px: 2.5,
                  py: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    Order Number
                  </Typography>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "1.25rem",
                      fontFamily: "monospace",
                      lineHeight: 1.2,
                    }}
                  >
                    #{orderResult.orderNumber}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.6,
                      bgcolor: "rgba(255,255,255,0.18)",
                      borderRadius: 2,
                      px: 1.25,
                      py: 0.4,
                      border: "1px solid rgba(255,255,255,0.25)",
                    }}
                  >
                    <OrderTypeIcon sx={{ fontSize: 14, color: "#fff" }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "#fff", fontWeight: 700 }}
                    >
                      {otc.label}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                    <AccessTime
                      sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.72)" }}
                    >
                      {moment(orderResult.createdAt).format("hh:mm A, MMM DD")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Customer info row */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.75,
                  display: "flex",
                  gap: 0,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PersonOutlined
                      sx={{ fontSize: 18, color: theme.palette.primary.main }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: "0.62rem",
                      }}
                    >
                      Customer
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, mt: -0.1 }}
                    >
                      {orderResult.customerName}
                    </Typography>
                  </Box>
                </Box>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 2, opacity: 0.5 }}
                />
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PhoneOutlined
                      sx={{ fontSize: 18, color: theme.palette.info.main }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: "0.62rem",
                      }}
                    >
                      Mobile
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, mt: -0.1 }}
                    >
                      {orderResult.customerMobile}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Special instructions (inside overview card) */}
              {orderResult.specialInstructions && (
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    display: "flex",
                    gap: 1.25,
                    alignItems: "flex-start",
                    bgcolor: alpha(theme.palette.warning.main, 0.04),
                  }}
                >
                  <SpeakerNotes
                    sx={{
                      fontSize: 17,
                      color: theme.palette.warning.dark,
                      mt: 0.2,
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.warning.dark,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        fontSize: "0.6rem",
                        display: "block",
                      }}
                    >
                      Special Instructions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {orderResult.specialInstructions}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* ═══ ITEMS ORDERED ═══ */}
            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: theme.palette.background.paper,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: 2.5,
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.025),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <RestaurantMenuOutlined
                    sx={{ fontSize: 16, color: theme.palette.primary.main }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                  >
                    Items Ordered
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: 2,
                    px: 1,
                    py: 0.2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 800, color: theme.palette.primary.main }}
                  >
                    {orderResult.OrderItems?.length || 0} items
                  </Typography>
                </Box>
              </Box>

              {/* Item rows */}
              {(orderResult.OrderItems || []).map((item, idx) => {
                const unitPrice = parseFloat(item.unitPrice || 0);
                const totalPrice = parseFloat(item.totalPrice || 0);
                const itemName = item.MenuItem?.name || "Item";
                const imagePath = item.MenuItem?.image || null;
                const isLast =
                  idx === (orderResult.OrderItems?.length || 0) - 1;
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      borderBottom: isLast
                        ? "none"
                        : `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                    }}
                  >
                    {/* Image */}
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        flexShrink: 0,
                        overflow: "hidden",
                        bgcolor: alpha(theme.palette.text.secondary, 0.06),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      }}
                    >
                      {imagePath ? (
                        <ImageLoader
                          imagePath={imagePath}
                          width={64}
                          height={64}
                          alt={itemName}
                          rounded={false}
                        />
                      ) : (
                        <RestaurantMenuOutlined
                          sx={{
                            fontSize: 28,
                            color: alpha(theme.palette.text.secondary, 0.25),
                          }}
                        />
                      )}
                    </Box>

                    {/* Name + sub info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, mb: 0.15 }}
                      >
                        {itemName}
                      </Typography>
                      {item.ItemVariation && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                          }}
                        >
                          {item.ItemVariation.name}
                        </Typography>
                      )}
                      {item.OrderItemModifiers?.length > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          +{" "}
                          {item.OrderItemModifiers.map((m) => m.name).join(
                            ", ",
                          )}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.disabled">
                        {formatCurrency(unitPrice, activeCurrency)} each
                      </Typography>
                    </Box>

                    {/* Qty + price column */}
                    <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                      <Box
                        sx={{
                          display: "inline-block",
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          borderRadius: 1.5,
                          px: 1,
                          py: 0.2,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            color: theme.palette.primary.main,
                          }}
                        >
                          x{item.quantity}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: theme.palette.text.primary,
                          display: "block",
                        }}
                      >
                        {formatCurrency(totalPrice, activeCurrency)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* ═══ BILL SUMMARY ═══ */}
            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: theme.palette.background.paper,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.25,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.025),
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Bill Summary
                </Typography>
              </Box>
              <Box sx={{ px: 2.5, pt: 1.75, pb: 0.5 }}>
                {[
                  { label: "Subtotal", value: subtotal, show: true },
                  {
                    label: "Service Charge",
                    value: serviceCharge,
                    show: serviceCharge > 0,
                  },
                  { label: "VAT", value: vatAmount, show: vatAmount > 0 },
                ]
                  .filter((r) => r.show)
                  .map((row) => (
                    <Box
                      key={row.label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1.25,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {row.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(row.value, activeCurrency)}
                      </Typography>
                    </Box>
                  ))}
                {discountAmount > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1.25,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                      Discount
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#2e7d32" }}
                    >
                      -{formatCurrency(discountAmount, activeCurrency)}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={{ mx: 2.5 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2.5,
                  py: 1.75,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Total
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    lineHeight: 1,
                  }}
                >
                  {formatCurrency(totalAmount, activeCurrency)}
                </Typography>
              </Box>
            </Box>

            {/* ═══ PRINT RECEIPT CTA ═══ */}
            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                background:
                  "linear-gradient(135deg, #7b4f00 0%, #b45309 40%, #d97706 100%)",
                boxShadow: "0 4px 20px rgba(180,83,9,0.35)",
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <ReceiptLong sx={{ fontSize: 28, color: "#fff" }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{ color: "#fff", fontWeight: 800, lineHeight: 1.2 }}
                  >
                    Print Receipt
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.8)" }}
                  >
                    Get a printed copy of your order details
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Print />}
                  onClick={handlePrint}
                  sx={{
                    flexShrink: 0,
                    bgcolor: "#fff",
                    color: "#b45309",
                    fontWeight: 800,
                    textTransform: "none",
                    borderRadius: 2,
                    px: 2.5,
                    py: 1.1,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                    "&:hover": { bgcolor: "#fff9f0" },
                  }}
                >
                  Print
                </Button>
              </Box>
            </Box>

            {/* ═══ THANK YOU ═══ */}
            <Box
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                textAlign: "center",
                px: 3,
                py: 3,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #1b5e20, #43a047)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                  boxShadow: "0 4px 16px rgba(46,125,50,0.35)",
                }}
              >
                <Favorite sx={{ fontSize: 28, color: "#fff" }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>
                Thank you for your order!
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: 360, mx: "auto", lineHeight: 1.7 }}
              >
                Your <strong>{otc.label}</strong> order is being prepared with
                care. We will notify you when it is ready.
              </Typography>
            </Box>

            {/* Bottom spacing */}
            <Box sx={{ height: 8 }} />
          </Box>
        </DialogContent>

        {/* ── STICKY FOOTER ── */}
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 1.75,
            bgcolor: theme.palette.background.paper,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box
            sx={{
              maxWidth: 680,
              mx: "auto",
              display: "grid",
              gap: 1,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={handleTrackOrder}
              disabled={!trackOrderId || !trackCompanyId}
              sx={{
                py: 1.5,
                fontWeight: 800,
                textTransform: "none",
                borderRadius: 2.5,
                fontSize: "0.95rem",
              }}
            >
              Track Order
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={onClose}
              sx={{
                py: 1.5,
                fontWeight: 800,
                textTransform: "none",
                borderRadius: luxury ? "999px" : 2.5,
                fontSize: "0.95rem",
                background: luxury
                  ? chrome.gradient
                  : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: luxury
                  ? chrome.shadow
                  : `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                "&:hover": {
                  background: luxury ? chrome.gradientHover : undefined,
                  boxShadow: luxury
                    ? chrome.shadowHover
                    : `0 6px 18px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              Back to Menu
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default OrderSuccessDialog;
